/**
 * What the hub page is showing, derived from facts rather than from `if`s
 * scattered through the component (MAR-219).
 *
 * Nine states, and the reason they are nine and not "loading, error, ready" is
 * that six of them want different words and a different button. "Sign in",
 * "add your date", "your date has aged out", "you do not own this stage",
 * "Maria has not written it", "it is written and empty" and "something broke"
 * are seven different messages, and collapsing any two of them tells a
 * customer something untrue about her own account.
 *
 * Pure, and takes every fact as an argument, so all nine are reachable from a
 * test without a network, a clock or a React tree.
 */

import {
  MumHubNotEntitledError,
  MumHubSignedOutError,
  MumHubStageNotPublishedError,
  type MumHubStageContent,
} from "./api";
import type { MumHubStageProgress, MumHubStageResult } from "./stage";
import type { MumHubStageId } from "./stages";

export type MumHubHubState =
  | { kind: "loading" }
  | { kind: "signed-out" }
  /** Signed in, no due date on file. She has never set Mum Hub up. */
  | { kind: "no-date" }
  /** A date on file that is more than three months old. */
  | { kind: "window-ended" }
  /** Signed in, and this account does not have this stage or any of it. */
  | { kind: "not-entitled" }
  | {
      kind: "not-published";
      stage: MumHubStageId;
      progress: MumHubStageProgress | null;
    }
  | { kind: "unavailable" }
  | {
      kind: "ready";
      stage: MumHubStageId;
      progress: MumHubStageProgress | null;
      content: MumHubStageContent;
    };

export interface MumHubHubFacts {
  signedIn: boolean;
  /**
   * Null means NOT KNOWN YET, and is not "she has no date". The distinction is
   * the whole reason `profileLoaded` exists beside it: without that flag, the
   * first render of a signed-in customer with a date on file would flash the
   * "add your date" page before the profile arrived.
   */
  stageResult: MumHubStageResult | null;
  profileLoaded: boolean;
  /** Whether the profile that loaded actually carried a date. */
  hasDueDate: boolean;
  stageProgress?: MumHubStageProgress | null;
  content:
    | { status: "idle" }
    | { status: "pending" }
    | { status: "error"; error: unknown }
    | { status: "success"; data: MumHubStageContent };
}

export function deriveMumHubHubState(facts: MumHubHubFacts): MumHubHubState {
  if (!facts.signedIn) return { kind: "signed-out" };
  if (!facts.profileLoaded) return { kind: "loading" };
  if (!facts.hasDueDate) return { kind: "no-date" };
  // A date on file that produced nothing is a date this module cannot read,
  // which is a bug rather than a state she can reach, and there is nothing
  // honest to draw for it. Treated as the general fault.
  if (facts.stageResult === null) return { kind: "unavailable" };
  if (facts.stageResult.kind === "window-ended") return { kind: "window-ended" };

  const stage = facts.stageResult.stage;
  const progress = facts.stageProgress ?? null;

  if (facts.content.status === "idle" || facts.content.status === "pending") {
    return { kind: "loading" };
  }
  if (facts.content.status === "error") {
    const { error } = facts.content;
    // A session that died between the profile read and the content read.
    if (error instanceof MumHubSignedOutError) return { kind: "signed-out" };
    if (error instanceof MumHubNotEntitledError) return { kind: "not-entitled" };
    if (error instanceof MumHubStageNotPublishedError) {
      return { kind: "not-published", stage, progress };
    }
    return { kind: "unavailable" };
  }
  return { kind: "ready", stage, progress, content: facts.content.data };
}
